import React, { FunctionComponent, ReactNode } from 'react'

import { BlurView } from 'expo-blur'
import { LinearGradient } from 'expo-linear-gradient'
import styled, { useTheme } from 'styled-components/native'
import tinyColor from 'tinycolor2'

import { IS_WEB } from '../../constants'
import { AuroraThemeType } from '../../utils/themes/ThemeProvider'

const absoluteFillStyle = {
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
} as const

const GradientFooterStyled = styled(LinearGradient)({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
})

const Wrapper = styled.View({
  position: 'relative',
  width: '100%',
  ...(IS_WEB && {
    backdropFilter: 'blur(2px)',
    WebkitBackdropFilter: 'blur(2px)',
  }),
})

export interface AuroraFooterWrapperProps {
  children: ReactNode
  /** Enable blur effect for Aurora theme. Automatically detects theme if not provided. */
  enableBlur?: boolean
}

/**
 * Wraps footer content with Aurora-themed blur and gradient background.
 * Automatically applies effects only when Aurora theme is active.
 *
 * The gradient creates a fade from transparent to the theme's background color,
 * matching the Figma designs for Aurora Light and Aurora Dark themes.
 *
 * @example
 * ```tsx
 * <StickyView
 *   footerComponent={
 *     <AuroraFooterWrapper>
 *       <YourFooterContent />
 *     </AuroraFooterWrapper>
 *   }
 * />
 * ```
 */
export const AuroraFooterWrapper: FunctionComponent<AuroraFooterWrapperProps> = ({ children, enableBlur }) => {
  const { colors, isDarkMode, isAuroraTheme } = useTheme() as AuroraThemeType

  const shouldApplyBlur = (enableBlur ?? isAuroraTheme) && isAuroraTheme
  if (!shouldApplyBlur) {
    return <>{children}</>
  }

  const baseColor = (colors as AuroraThemeType['colors']).gradientBackgroundFooter
  const gradientColors: [string, string, string] = [
    tinyColor(baseColor).setAlpha(0).toRgbString(), // 0% opacity - fully transparent
    tinyColor(baseColor).setAlpha(0.9).toRgbString(), // 90% opacity - semi-transparent
    baseColor, // 100% opacity - solid color
  ]

  return (
    <Wrapper>
      {!IS_WEB && <BlurView intensity={2} tint={isDarkMode ? 'dark' : 'light'} style={absoluteFillStyle} />}
      <GradientFooterStyled
        colors={gradientColors}
        start={{ x: 0.5, y: 0 }}
        end={{ x: 0.5, y: 1 }}
        locations={[0, 0.2, 1]}
      />
      {children}
    </Wrapper>
  )
}
